import { parseOptions, createError, createSuccess } from "./utils.jsx";
import { TYPES } from "../segments/FileSystem";

const Errors = {
  INVALID_PATH: (path = "") => `Path ${path} not valid`,
  DIR_NOT_FOUND: (dir = "") => `Directory ${dir} not found`,
  NODE_NOT_FOUND: (node = "") => `Directory ${node} not found`,
  OPT_NOT_VALID: (opt = "") => `Option ${opt} not valid`,
  ARG_MISSING: (arg = "") => `Argument '${arg}' not specified`,
  ARG_REDUNDANT: () => `More arguments than expected`,
  NODE_NOT_REMOVED: (name) => `Node '${name}' cannot be removed`,
};

const Successes = {
  NODE_REMOVED: (name) => `Node '${name}' removed`,
};

const parsePath = (pathStr, oPathArr) => {
  oPathArr = oPathArr.slice();
  if (pathStr.length === 0) {
    return createError(Errors.INVALID_PATH(pathStr));
  }
  if (pathStr[0] === "/") {
    oPathArr = ["root"];
  }
  const rPathArr = pathStr.split("/").filter((e) => e !== "");
  for (let entry of rPathArr) {
    if (entry === ".") {
      continue;
    } else if (entry === "..") {
      if (oPathArr.length <= 1) {
        return createError(Errors.INVALID_PATH(pathStr));
      } else {
        oPathArr.pop();
      }
    } else {
      oPathArr.push(entry);
    }
  }
  return createSuccess(oPathArr, true);
};

const changePath = (newPath, system) => {
  const path = newPath.slice();
  const dir = system.getFileSystem().getDirByPath(path);
  if (!dir) {
    return createError(Errors.DIR_NOT_FOUND(newPath.join("/")));
  } else {
    system.setPath(newPath);
    system.setDirectory(dir);
    return createSuccess();
  }
};

const createPath = (newPath, system) => {
  const path = newPath.slice();
  const fileSystem = system.getFileSystem();
  const dirName = path.pop();
  const parentDir = fileSystem.getDirByPath(path);
  if (!parentDir) {
    return createError(Errors.DIR_NOT_FOUND(path.join("/")));
  } else {
    const dirHash = fileSystem.createNewDirectory(parentDir.getHash(), dirName);
    system.setPath(newPath);
    system.setDirectory(fileSystem.getDirectory(dirHash));
    return createSuccess();
  }
};

/**
 * Change the system directory
 * @param {Array} cmds
 * @param {SystemAPI} system
 * @returns
 */
const changeDirectory = (cmds, system) => {
  const { options, args } = parseOptions(cmds);
  let cFlag = false;
  let hFlag = false;
  let res = null;
  for (let opt of options) {
    if (opt === "c") {
      cFlag = true;
    } else if (opt === "h") {
      hFlag = true;
    } else {
      return createError(Errors.OPT_NOT_VALID(opt));
    }
  }
  if (args.length === 0) {
    if (hFlag) {
      return createSuccess(fs.cd.help, true);
    }
    return createError(Errors.ARG_MISSING("directory"));
  }
  if (args.length > 1) {
    return createError(Errors.ARG_REDUNDANT());
  }
  const rPath = args[0];
  let oPathArr = system.getPath();
  if (hFlag) {
    return createError(Errors.ARG_REDUNDANT());
  }
  res = parsePath(rPath, oPathArr);
  if (res.error) {
    return createError(res.errMsg);
  }
  const pathArr = res.msg;
  if (cFlag) {
    res = createPath(pathArr, system);
  }
  res = changePath(pathArr, system);
  if (res.error) {
    return createError(res.errMsg);
  } else {
    return createSuccess(
      ["Path changed", "Current Path: " + pathArr.join("/")],
      true
    );
  }
};

/**
 * Remove node from file system
 * @param {Array} cmds
 * @param {SystemAPI} system
 * @returns
 */
const removeNode = (cmds, system) => {
  const { options, args } = parseOptions(cmds);
  let dFlag = false;
  let res = null;
  const fs = system.getFileSystem();
  const oPathArr = system.getPath();
  for (let opt of options) {
    if (opt === "d") {
      dFlag = true;
    } else {
      return createError(Errors.OPT_NOT_VALID(opt));
    }
  }
  if (args.length === 0) {
    return createError(Errors.ARG_MISSING("node"));
  }

  let resArr = [];
  for (let pathStr of args) {
    res = parsePath(pathStr, oPathArr);
    if (!res) {
      return createError(res.errMsg);
    } else {
      const pathArr = res.msg;
      const node = fs.getNodeByPath(pathArr);
      if (!node) {
        return createError(Errors.NODE_NOT_FOUND(pathArr.join("/")));
      } else {
        res = fs.removeNode(node.getParent(), node.getHash());
        if (dFlag) {
          fs.deepRemoveNode(node.getHash());
        }
        if (res) {
          resArr.push(Successes.NODE_REMOVED(node.getHash()));
        } else {
          resArr.push(Errors.NODE_NOT_REMOVED(node.getHash()));
        }
      }
    }
  }
  return createSuccess(resArr, true);
};

/**
 * List current directory info
 * @param {Array} cmds
 * @param {SystemAPI} system
 * @returns
 */
const listDirectory = (cmds, system) => {
  const { options, args } = parseOptions(cmds);
  let lFlag = false;
  let res = null;
  const oPathArr = system.getPath();
  const fs = system.getFileSystem();
  for (let opt of options) {
    if (opt === "l") {
      lFlag = true;
    } else {
      return createError(Errors.OPT_NOT_VALID(opt));
    }
  }

  let children = [];
  if (args.length === 0) {
    const dir = fs.getDirByPath(oPathArr);
    if (!dir) {
      return createError(Errors.DIR_NOT_FOUND(oPathArr.join("/")));
    }
    children = fs.retrieveDirectoryContent(dir.getHash());
  } else if (args.length > 1) {
    return createError(Errors.ARG_REDUNDANT());
  } else {
    const rPath = args[0];
    res = parsePath(rPath, oPathArr);
    if (res.error) {
      return createError(res.errMsg);
    } else {
      const pathArr = res.msg;
      const dir = fs.getDirByPath(pathArr);
      if (!dir) {
        return createError(Errors.DIR_NOT_FOUND(pathArr.join("/")));
      } else {
        children = fs.retrieveDirectoryContent(dir.getHash());
      }
    }
  }
  if (lFlag) {
    return createSuccess(
      Object.values(children).map(
        (child) =>
          `${child.name} ${child.hash} ${
            child.type === TYPES.FILE ? "FILE" : "DIR"
          } ${
            child.type === TYPES.FILE
              ? fs.getFile(child.hash).getSize()
              : fs.getDirectory(child.hash).getChildrenCount()
          }`
      ),
      true
    );
  } else {
    return createSuccess(
      Object.values(children).map((child) => child.name),
      true
    );
  }
};

const fs = {
  cd: {
    exec: changeDirectory,
    help: [
      "cd [OPTIONS] directory",
      " [OPTIONS]:",
      " -c: create the specified directory",
      " -h: show help message",
    ],
  },
  ls: {
    exec: listDirectory,
    help: [
      "ls [OPTIONS] [directory]",
      " [OPTIONS]:",
      " -l: list all the information",
    ],
  },
  rm: {
    exec: removeNode,
    help: [
      "rm [OPTIONS] [directory | file]",
      " [OPTIONS]:",
      " -d: deep remove (not in the bin)",
    ],
  },
};

export default fs;
